import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import AthleteLayout from "../common/AthleteLayout";
import { AthleteHeader } from "../common/AthleteHeader";
import { useAthleteAuthContext } from "../login/useAthleteAuthContext";
import API_URL from "../../api/API";

import workout from "../../images/workout.png";
import demo from "../../images/demo.png";
import video from "../../images/video.svg";
import plus from "../../images/plus.svg";
import upload from "../../images/upload.svg";
import greenClock from "../../images/greenClock.svg";

export const AthleteCoachedSpeedWorkoutSprints = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { athlete } = useAthleteAuthContext();

  const [actualTimes, setActualTimes] = useState({});

  // coached version expects week/day/sprints from state (parent should pass week, day, sprints)
  const { week, day, sprints, parentWeeks } = location.state || {};

  console.log(sprints);

  const [speedWorkout, setSpeedWorkout] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [showVideo, setShowVideo] = useState(null);

  const [uploadingSprintId, setUploadingSprintId] = useState(null);

  // Messaging states (wired to working API logic from product version)
  const [showMessagePopup, setShowMessagePopup] = useState(false);
  const [message, setMessage] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  const [localSprints, setLocalSprints] = useState(sprints || []);

  const [savingSprintId, setSavingSprintId] = useState(null);

  const totalDistance = sprints?.reduce(
    (sum, sprint) =>
      sum + (sprint.sets || 0) * (sprint.reps || 0) * (sprint.distance || 0),
    0,
  );

  const handleAccordionClick = (index) =>
    setSpeedWorkout(speedWorkout === index ? null : index);

  // BACK: navigate to parent route but pass state so parent can reuse weeks and avoid refetch
  const handleBackClick = () => {
    // create new updated parentWeeks with updated sprints injected
    const updatedWeeks = parentWeeks.map((w) => {
      if (w._id !== week._id) return w;

      return {
        ...w,
        days: w.days.map((d) => {
          if (d._id !== day._id) return d;

          return { ...d, sprints: localSprints }; // ⭐ inject updated sprint times
        }),
      };
    });

    // navigate back WITH updated weeks + flag
    navigate("/athlete/coached/speed-workout", {
      state: {
        initialWeek: week,
        initialDay: { ...day, sprints: localSprints },
        initialWeeks: updatedWeeks,
        persistUpdatedWeeks: true, // ⭐ tell parent to permanently update state
      },
    });
  };

  const onFilesChange = (files) => {
    const arr = Array.from(files || []);
    setAttachments((prev) => [...prev, ...arr]);
  };

  const removeAttachment = (idx) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSendMessage = async (e) => {
    e?.preventDefault?.();
    if (!message.trim() && attachments.length === 0) return;

    const workoutContext = `
[Workout Context]
Coached Workout
Day: ${day?.date || day?.name || "N/A"}
${
  speedWorkout !== null && sprints?.[speedWorkout]?.type !== "Rest"
    ? `Sprint: Sets ${sprints[speedWorkout].sets} × Reps ${sprints[speedWorkout].reps} × Distance ${sprints[speedWorkout].distance}m, Intensity ${sprints[speedWorkout].intensity}`
    : "Sprint: General Workout Message"
}
User Message:
${message}
    `;

    const formData = new FormData();
    formData.append("sender_id", athlete.id);
    formData.append("message", workoutContext.trim());
    formData.append("sender_type", "athlete");
    formData.append("receiver_type", "amax");
    formData.append("receiver_id", "amax");

    attachments.forEach((file) => formData.append("attachments", file));

    setSending(true);

    try {
      const res = await fetch(API_URL.SEND_ATHLETE_MESSAGE_TO_AMAX, {
        method: "POST",
        headers: { Authorization: `Bearer ${athlete.token}` },
        body: formData,
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setMessage("");
        setAttachments([]);
        setShowMessagePopup(false);
        setSuccessMsg("Message sent successfully!");
        setTimeout(() => setSuccessMsg(""), 3000);
      } else {
        alert(data.error || "Failed to send message");
      }
    } catch (err) {
      console.error("Error sending message:", err);
      alert("Error sending message");
    } finally {
      setSending(false);
    }
  };

  // Add new state for global alerts
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState(""); // "success" or "error"

  // --- Save Actual Time ---
  const saveActualTime = async (sprintId) => {
    const timeValue = actualTimes[sprintId];
    if (!timeValue) return;

    setSavingSprintId(sprintId); // ⭐ show spinner for this sprint
    setAlertMessage("Saving actual time...");
    setAlertType("info");

    try {
      const res = await fetch(
        `${API_URL.FRONTEND_COACHED_ATHLETE_SAVE_ACTUAL_TIME}${athlete.id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${athlete.token}`,
          },
          body: JSON.stringify({
            day_id: day._id,
            sprint_id: sprintId,
            actual_time: timeValue,
          }),
        },
      );

      const data = await res.json();

      if (res.ok) {
        setLocalSprints((prev) =>
          prev.map((s) =>
            s._id === sprintId ? { ...s, actual_time: timeValue } : s,
          ),
        );

        setAlertMessage("Actual time saved successfully!");
        setAlertType("success");
        setTimeout(() => setAlertMessage(""), 3000);
      } else {
        setAlertMessage(data.error || "Failed to save actual time");
        setAlertType("error");
        setTimeout(() => setAlertMessage(""), 3000);
      }
    } catch (err) {
      console.error("Error saving actual time:", err);
      setAlertMessage("Error saving actual time");
      setAlertType("error");
      setTimeout(() => setAlertMessage(""), 3000);
    } finally {
      setSavingSprintId(null); // hide spinner
    }
  };

  // --- Video Upload ---
  const handleVideoUpload = async (sprintId, file) => {
    if (!file) return;

    setUploadingSprintId(sprintId); // show spinner
    setAlertMessage("Uploading video...");
    setAlertType("info");

    const formData = new FormData();
    formData.append("day_id", day._id);
    formData.append("sprint_id", sprintId);
    formData.append("video", file);

    try {
      const res = await fetch(
        `${API_URL.FRONTEND_COACHED_ATHLETE_SAVE_VIDEO}${athlete.id}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${athlete.token}`,
          },
          body: formData,
        },
      );

      const data = await res.json();

      if (res.ok) {
        setLocalSprints((prev) =>
          prev.map((s) =>
            s._id === sprintId ? { ...s, video_path: data.video_path } : s,
          ),
        );
        setAlertMessage("Video uploaded successfully!");
        setAlertType("success");
        setTimeout(() => setAlertMessage(""), 3000);
      } else {
        setAlertMessage(data.error || "Failed to upload video");
        setAlertType("error");
        setTimeout(() => setAlertMessage(""), 3000);
      }
    } catch (err) {
      console.error("Error uploading video:", err);
      setAlertMessage("Error uploading video");
      setAlertType("error");
      setTimeout(() => setAlertMessage(""), 3000);
    } finally {
      setUploadingSprintId(null); // hide spinner
    }
  };

  return (
    <AthleteLayout>
      <div className="h-full flex flex-col bg-white">
        <div className="flex-1 overflow-y-auto">
          <div className="sticky top-[0px] z-10 bg-white pb-2">
            <div>
              <AthleteHeader />
            </div>

            <div className="relative w-full max-w-2xl mx-auto px-3 xs:px-5 pt-3">
              <div className="bg-[#f5f4f4] rounded-2xl flex justify-between gap-8 px-4 py-4 mb-5">
                <button
                  onClick={() =>
                    navigate("/athlete/coached/warmup-routine-phases", {
                      state: { week, day, sprints },
                    })
                  }
                  className="text-lightSmock text-sm xs:text-base"
                >
                  Warmup
                </button>
                <button className="text-black text-sm xs:text-base">
                  Workout
                </button>
              </div>

              {/* Banner / Info Toggle identical to product version */}
              <div
                className={`${
                  speedWorkout !== null ? "block" : "relative"
                } relative mb-5`}
              >
                {speedWorkout === null && (
                  <div className="flex justify-between items-center text-white w-full bg-black rounded-xl p-3 my-3">
                    <p className="text-base xs:text-xl font-medium">{`${day?.date || ""}`}</p>
                    <p className="text-xs xs:text-sm font-medium">
                      {totalDistance} m
                    </p>
                  </div>
                )}

                {speedWorkout !== null && (
                  <div className="flex justify-between items-center text-white w-full bg-black rounded-xl p-3 my-3">
                    <p className="text-base xs:text-xl font-medium">{` ${day?.date || ""}`}</p>
                    <p className="text-xs xs:text-sm font-medium">
                      {totalDistance} m
                    </p>
                  </div>
                )}
              </div>
              {/* label header */}
              <div className="w-full max-w-2xl mx-auto mb-5">
                <div className="bg-black rounded-xl py-3 px-3 pr-7 xs:pr-8 divide-x-2 divide-white/50 flex flex-row justify-between items-stretch text-center relative z-10">
                  <div className="flex-1 px-2">
                    <p className="text-[#fcfbfb] text-[10px] xs:text-xs font-light uppercase">
                      Sets
                    </p>
                  </div>
                  <div className="flex-1 px-2">
                    <p className="text-[#fcfbfb] text-[10px] xs:text-xs font-light uppercase">
                      Reps
                    </p>
                  </div>
                  <div className="flex-1 px-2">
                    <p className="text-[#fcfbfb] text-[10px] xs:text-xs font-light uppercase">
                      Distance
                    </p>
                  </div>
                  <div className="flex-1 px-2">
                    <p className="text-[#fcfbfb] text-[10px] xs:text-xs font-light uppercase">
                      Intensity
                    </p>
                  </div>
                  <div className="flex-1 px-2">
                    <p className="text-[#fcfbfb] text-[10px] xs:text-xs font-light uppercase">
                      T/T
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="w-full max-w-2xl mx-auto">
            <div className="flex flex-col gap-5 px-3 xs:px-5">
              {localSprints?.length ? (
                localSprints.map((item, index) => (
                  <div
                    key={index}
                    className="bg-transparent py-3 px-3 rounded-xl border border-black/30 bg-white"
                  >
                    {item.exercise_id?.title && (
                      <div className="flex justify-between items-center gap-3 mb-2 py-1 pl-3 border-l-2 border-darkYellow cursor-pointer">
                        <p className="text-black text-base font-semibold mb-2">
                          {item.exercise_id.title}
                        </p>
                      </div>
                    )}

                    {item.type === "Rest" ? (
                      <div className="flex flex-row justify-center items-center text-center text-black">
                        <div className="space-y-1">
                          <span className="text-lightSmock text-xs uppercase">
                            Rest
                          </span>
                          <p className="text-black">{item.rest_notes}</p>
                        </div>
                      </div>
                    ) : (
                      <div
                        className="flex flex-row justify-between items-stretch text-center cursor-pointer text-black max-xs:-mx-2"
                        onClick={() => handleAccordionClick(index)}
                      >
                        <div className="max-xs:h-auto flex-1 flex justify-center items-center px-1 border-r border-black">
                          <p className="max-xs:text-xs font-light">
                            {item.sets}
                          </p>
                        </div>
                        <div className="max-xs:h-auto flex-1 flex justify-center items-center px-1 border-r border-black">
                          <p className="max-xs:text-xs font-light">
                            {item.reps}
                          </p>
                        </div>
                        <div className="max-xs:h-auto flex-1 flex justify-center items-center px-1 border-r border-black">
                          <p className="max-xs:text-xs font-light">
                            {item.distance}
                          </p>
                        </div>
                        <div className="max-xs:h-auto flex-1 flex justify-center items-center px-1 border-r border-black">
                          <p className="max-xs:text-xs font-light">
                            {item.intensity}
                          </p>
                        </div>
                        <div className="max-xs:h-auto flex-1 flex justify-center items-center px-1">
                          <span className="relative group">
                            <p className="max-xs:text-xs font-light">
                              {item.target_time}
                            </p>
                          </span>
                        </div>
                        <div className="w-5 h-5 bg-darkYellow text-black flex justify-center items-center p-1 cursor-pointer my-auto">
                          {speedWorkout === index ? (
                            <>
                              <Minus />
                            </>
                          ) : (
                            <>
                              <Plus />
                            </>
                          )}
                        </div>
                      </div>
                    )}

                    {item.type !== "Rest" && speedWorkout === index && (
                      <>
                        <div className="pt-3 mt-5 border-t-2 border-darkYellow"></div>
                        <div className="text-black text-sm font-medium mb-3">
                          {item.rest && (
                            <p>
                              <span className="uppercase">Rest: </span>
                              {item.rest}
                            </p>
                          )}
                          {item.notes && (
                            <p className="whitespace-pre-line">
                              <span className="uppercase">Notes: </span>
                              {item.notes}
                            </p>
                          )}
                        </div>

                        <div className="w-full flex flex-row gap-4 mb-4">
                          <div
                            className="w-1/2 h-[120px] border border-black/30 rounded-2xl overflow-hidden cursor-pointer"
                            onClick={() =>
                              item.exercise_id?.video_url &&
                              setShowVideo(item.exercise_id.video_url)
                            }
                          >
                            {item.exercise_id?.video_url ? (
                             (() => {
                              const videoId = getYoutubeVideoId(item.exercise_id.video_url);
                            
                              return (
                                <img
                                  src={
                                    videoId
                                      ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
                                      : demo
                                  }
                                  alt={item.exercise_id.title}
                                  className="object-cover w-full h-full"
                                />
                              );
                            })()
                            ) : (
                              <img
                                src={demo}
                                alt="demo"
                                className="object-cover object-[100%_100%]"
                              />
                            )}
                          </div>

                          <div className="w-1/2 h-[120px] border border-black/30 p-4 rounded-2xl overflow-hidden">
                            <img
                              src={video}
                              alt="video"
                              className="object-contain mb-6 cursor-pointer"
                            />
                            <p className="text-black text-sm font-medium mb-3">
                              Upload video
                            </p>

                            <label className="cursor-pointer flex items-center gap-2">
                              {uploadingSprintId === item._id ? (
                                <svg
                                  className="animate-spin h-6 w-6 text-black"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  />
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4l-3 3 3 3H4z"
                                  />
                                </svg>
                              ) : (
                                <img
                                  src={upload}
                                  alt="upload"
                                  className="object-contain cursor-pointer invert"
                                />
                              )}

                              <input
                                type="file"
                                accept="video/*"
                                onChange={(e) =>
                                  handleVideoUpload(item._id, e.target.files[0])
                                }
                                className="hidden"
                              />
                            </label>
                          </div>
                        </div>

                        <div className="max-xs:px-0 p-3 flex justify-between items-center">
                          <p className="text-black text-base xs:text-xl font-medium">
                            Actual Time
                          </p>
                          <img
                            src={greenClock}
                            alt="greenClock"
                            className="object-contain cursor-pointer w-5"
                          />
                        </div>

                        <div className="max-xs:px-0 p-3 pt-0 flex justify-between items-center">
                          <div className="relative w-full border-b border-black/30">
                            <input
                              type="text"
                              className="placeholder:text-[#5D5D5D] placeholder:font-medium text-black text-base font-light bg-transparent w-full outline-none p-2 pr-12"
                              placeholder="Actual time"
                              value={
                                actualTimes[item._id] ??
                                localSprints[index]?.actual_time ??
                                ""
                              }
                              onChange={(e) =>
                                setActualTimes((prev) => ({
                                  ...prev,
                                  [item._id]: e.target.value,
                                }))
                              }
                            />
                            {savingSprintId === item._id ? (
                              // ⭐ Spinner
                              <div className="w-6 absolute right-0 top-1/2 -translate-y-1/2 invert">
                                <svg
                                  className="animate-spin h-6 w-6 text-black"
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  ></circle>
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4l-3 3 3 3H4z"
                                  ></path>
                                </svg>
                              </div>
                            ) : (
                              // ⭐ Normal plus icon
                              <div className="absolute right-0 top-1/2 -translate-y-1/2 cursor-pointer rounded-full size-8 flex justify-center items-center bg-grayish/50 hover:bg-darkYellow transition group">
                                <img
                                  onClick={() => saveActualTime(item._id)}
                                  src={plus}
                                  alt="plus"
                                  className="w-4 h-4 invert"
                                />
                              </div>
                            )}
                          </div>
                        </div>

                        <button
                          onClick={() => setShowMessagePopup(true)}
                          className="bg-darkYellow w-full p-3 rounded-full block capitalize text-black text-base xs:text-xl font-medium mt-5"
                        >
                          Message the Coach
                        </button>
                      </>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-center py-10 text-black">
                  No sprints found for this day.
                </p>
              )}
            </div>
          </div>

          {showPopup && (
            <PopupMessage
              message="This is only available to Amax coached athletes!"
              onClose={() => setShowPopup(false)}
            />
          )}

          {alertMessage && (
            <div
              className={`fixed bottom-5 left-1/2 -translate-x-1/2 px-6 py-3 rounded shadow-lg text-white z-50
              ${alertType === "success" ? "bg-green-600" : ""}
              ${alertType === "error" ? "bg-red-600" : ""}
              ${alertType === "info" ? "bg-blue-600" : ""}
            `}
            >
              {alertMessage}
            </div>
          )}

          {showMessagePopup && (
            <MessageCoachPopup
              onClose={() => setShowMessagePopup(false)}
              message={message}
              setMessage={setMessage}
              attachments={attachments}
              onFilesChange={onFilesChange}
              removeAttachment={removeAttachment}
              onSend={handleSendMessage}
              sending={sending}
              successMsg={successMsg}
            />
          )}

          {showVideo && (
            <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
              <div className="relative w-full max-w-3xl h-[60vh]">
              <iframe
                src={getEmbedUrl(showVideo)}
                title="Sprint Video"
                className="w-full h-full rounded-lg"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
              <button
                  onClick={() => setShowVideo(null)}
                  className="absolute top-2 right-2 text-white text-2xl bg-black bg-opacity-50 rounded-full p-2"
                >
                  ✕
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </AthleteLayout>
  );
};

const getEmbedUrl = (url) => {
  const videoId = getYoutubeVideoId(url);
  return videoId
    ? `https://www.youtube.com/embed/${videoId}`
    : "";
};

/* Helper components (same SVGs and popups you provided) */

const PopupMessage = ({ message, onClose }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
    <div className="bg-white p-5 rounded shadow-lg text-center">
      <p className="text-black text-sm font-semibold">{message}</p>
      <button
        onClick={onClose}
        className="mt-3 px-4 py-2 bg-black text-white rounded hover:opacity-80"
      >
        Close
      </button>
    </div>
  </div>
);

const MessageCoachPopup = ({
  onClose,
  message,
  setMessage,
  attachments,
  onFilesChange,
  removeAttachment,
  onSend,
  sending,
  successMsg,
}) => {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white p-4 xs:p-6 rounded-lg shadow-lg w-full max-w-md">
        <h2 className="text-black text-lg xs:text-xl font-bold mb-4">
          Message the Coach
        </h2>

        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows="4"
          className="w-full text-base font-medium border border-gray-300 p-2 rounded focus:outline-none focus:ring focus:ring-darkYellow text-black placeholder-gray-500 mb-3"
          placeholder="Type your message here..."
        />

        <div className="mb-3">
          <input
            type="file"
            multiple
            onChange={(e) => onFilesChange(e.target.files)}
            className="mb-2"
          />
          {attachments?.length > 0 && (
            <div className="space-y-2">
              {attachments.map((f, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between border p-2 rounded"
                >
                  <span className="text-sm">{f.name}</span>
                  <button
                    className="text-sm text-red-600"
                    onClick={() => removeAttachment(i)}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {successMsg && <div className="mb-2 text-green-600">{successMsg}</div>}

        <div className="mt-4 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-black rounded hover:bg-gray-300 transition"
          >
            Cancel
          </button>
          <button
            onClick={onSend}
            disabled={sending}
            className="px-4 py-2 bg-black text-white rounded hover:bg-black/70 disabled:opacity-50 transition"
          >
            {sending ? "Sending…" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
};

const ArrowWhite = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="14"
    height="8"
    viewBox="0 0 14 8"
    fill="none"
  >
    <path
      d="M1 0.999999L7 7L13 1"
      stroke="black"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ArrowLWhite = ({ onClick }) => (
  <svg
    onClick={onClick}
    className="hover:opacity-50 rotate-180 cursor-pointer"
    xmlns="http://www.w3.org/2000/svg"
    width="8"
    height="14"
    viewBox="0 0 8 14"
    fill="none"
  >
    <path
      d="M1 13L7 7L1 1"
      stroke="black"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Plus = () => {
  return (
    <svg
      width="24"
      className="w-full h-full"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      preserveAspectRatio="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M0 0H24V24H0V0Z" fill="#C19E42" />
      <path d="M11 4.5H13V19.5H11V4.5Z" fill="black" />
      <path d="M4.5 13V11H19.5V13H4.5Z" fill="black" />
    </svg>
  );
};

const Minus = () => {
  return (
    <svg
      width="24"
      className="w-full h-full"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      preserveAspectRatio="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M0 0H24V24H0V0Z" fill="#C19E42" />
      {/* <path d="M10.5 4H13.5V20H10.5V4Z" fill="black" /> */}
      <path d="M4.5 13V11H19.5V13H4.5Z" fill="black" />
    </svg>
  );
};

const getYoutubeVideoId = (url) => {
  if (!url) return null;

  // Standard watch URL
  const watchMatch = url.match(/[?&]v=([^&]+)/);
  if (watchMatch) return watchMatch[1];

  // Shorts URL
  const shortsMatch = url.match(/youtube\.com\/shorts\/([^?&/]+)/);
  if (shortsMatch) return shortsMatch[1];

  // youtu.be URL
  const shortUrlMatch = url.match(/youtu\.be\/([^?&/]+)/);
  if (shortUrlMatch) return shortUrlMatch[1];

  return null;
};
